import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import './SearchBar.css';

// ✅ Fonction pour charger l’image selon le type de chambre
const getImageForRoomType = (type) => {
  switch (type) {
    case "Chambre single":
      return require("./assets/chambreSingle.jpg");
    case "Chambre Double":
      return require("./assets/chambre1.jpg");
    case "Chambre Triple":
      return require("./assets/triple.jpg");
    case "Suite Quadruple":
      return require("./assets/suiteRouge.jpg");
    case "Suite Communicante":
      return require("./assets/chambre7.jpg");
    default:
      return require("./assets/chambre1.jpg");
  }
};

// ✅ Descriptions par type de chambre
const roomDescriptions = {
  'Chambre single': [
    "Superficie de 20 à 25 m²",
    "Literie haut de gamme pour un confort optimal",
    "Téléviseur plasma avec chaînes câblées",
    "Wi-Fi gratuit",
    "Réfrigérateur et coffre-fort",
    "Salle de bain privative",
    "Climatisation centralisée et chauffage",
    "Lits supplémentaires disponibles sur demande"
  ],
  'Chambre Double': [
    "Superficie de 20 à 25 m²",
    "Deux lits confortables ou un lit double",
    "Téléviseur plasma avec chaînes câblées",
    "Wi-Fi gratuit",
    "Réfrigérateur et coffre-fort",
    "Salle de bain privative",
    "Climatisation centralisée et chauffage"
  ],
  'Chambre Triple': [
    "Superficie de 25 à 30 m²",
    "Capacité : 3 personnes",
    "Téléviseur plasma avec chaînes câblées",
    "Wi-Fi gratuit",
    "Réfrigérateur et coffre-fort",
    "Salle de bain privative",
    "Climatisation centralisée et chauffage"
  ],
  'Suite Quadruple': [
    "Superficie de 30 à 35,5 m²",
    "Capacité : 4 personnes",
    "Salon intégré",
    "Literie haut de gamme",
    "Téléviseur plasma avec chaînes câblées",
    "Wi-Fi gratuit",
    "Réfrigérateur et coffre-fort",
    "Salle de bain privative",
    "Climatisation centralisée et chauffage"
  ],
  'Suite Communicante': [
    "Type F2 – 45 m²",
    "Capacité jusqu’à 6 personnes",
    "Salon séparé et espace détente",
    "Literie haut de gamme",
    "Téléviseur plasma avec chaînes câblées",
    "Wi-Fi gratuit",
    "Réfrigérateur et coffre-fort",
    "Salle de bain privative",
    "Climatisation centralisée et chauffage"
  ]
};

const SearchBar = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [checkInDate, setCheckInDate] = useState('');
  const [checkOutDate, setCheckOutDate] = useState('');
  const [adults, setAdults] = useState(1);
  const [children, setChildren] = useState(0);
  const [rooms, setRooms] = useState(1);
  const [roomType, setRoomType] = useState(location.state?.type || '');
  const [availableRooms, setAvailableRooms] = useState([]);
  const [loaded, setLoaded] = useState(false);

  // 🔁 Charger les chambres disponibles
  useEffect(() => {
    if (checkInDate && checkOutDate && checkOutDate > checkInDate) {
      fetchRooms();
    }
  }, [checkInDate, checkOutDate, adults, children, rooms, roomType]);

  const fetchRooms = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/rooms/available`, {
        params: { checkInDate, checkOutDate, adults, children, rooms, type: roomType },
      });
      setAvailableRooms(response.data.chambres);
    } catch (error) {
      console.error('Erreur lors de la recherche des chambres:', error);
    }
  };

  return (
    <div className='bodyPage'>
      {/* 🏨 En-tête luxueux */}
      <div className="luxury-header">
        <h1 className="luxury-title">Hôtel Syphax</h1>
        <p className="luxury-subtitle">
          Découvrez notre collection exclusive de suites et chambres d'exception
        </p>
      </div>

      {/* 🔎 Barre de recherche */}
      <div className="search-bar-container">
        <div className="search-bar">
          <div className="input-group">
            <label>Date d'entrée</label>
            <input
              type="date"
              value={checkInDate}
              min={new Date().toISOString().split('T')[0]}
              onChange={(e) => setCheckInDate(e.target.value)}
            />
          </div>

          <div className="input-group">
            <label>Date de sortie</label>
            <input
              type="date"
              value={checkOutDate}
              min={checkInDate}
              onChange={(e) => setCheckOutDate(e.target.value)}
            />
          </div>

          <div className="input-group">
            <label>Adultes</label>
            <input
              type="number"
              value={adults}
              min="1"
              onChange={(e) => setAdults(e.target.value)}
            />
          </div>

          <div className="input-group">
            <label>Enfants</label>
            <input
              type="number"
              value={children}
              min="0"
              onChange={(e) => setChildren(e.target.value)}
            />
          </div>

          <div className="input-group">
            <label>Type d'hébergement</label>
            <select value={roomType} onChange={(e) => setRoomType(e.target.value)}>
              <option value="">-- Tous les types --</option>
              <option value="Chambre single">Chambre Single</option>
              <option value="Chambre Double">Chambre Double</option>
              <option value="Chambre Triple">Chambre Triple</option>
              <option value="Suite Quadruple">Suite Quadruple</option>
              <option value="Suite Communicante">Suite Communicante</option>
            </select>
          </div>
        </div>

        {/* 🏠 Liste des chambres disponibles */}
        <div className="available-rooms">
          <h2>Chambres Disponibles</h2>
          {availableRooms.length > 0 ? (
            <ul>
              {availableRooms.slice(0, rooms).map((room) => (
                <li key={room._id}>
                  <img
                    src={getImageForRoomType(room.type)}
                    alt={`Chambre ${room.type}`}
                    loading="lazy"
                    onLoad={() => setLoaded(true)}
                    style={{
                      opacity: loaded ? 1 : 0,
                      transition: 'opacity 0.5s ease-in-out',
                    }}
                  />
                  <div className="room-info">
                    <h3>{room.type}</h3>

                    {/* 📝 Description dynamique */}
                    <div style={{ all: 'unset' }}>
                      {Array.isArray(roomDescriptions[room.type]) ? (
                        roomDescriptions[room.type].map((desc, index) => (
                          <div key={index}>{desc}</div>
                        ))
                      ) : (
                        <div>Description non disponible</div>
                      )}
                    </div>

                    <p>Prix: {room.prix_nuit}.00 DA / nuit</p>
                    <button
                      onClick={() =>
                        navigate('/confirmationForm', {
                          state: {
                            room,
                            checkInDate,
                            checkOutDate,
                            adults,
                            children,
                            rooms,
                          },
                        })
                      }
                    >
                      Réserver maintenant
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p>Aucune chambre disponible.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchBar;
